/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import Box from "@material-ui/core/Box";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";
import { useSelector } from "store";
import { isDisplayPeriod } from "utility";
import { isShippingItem } from "utility/ticket";

import type { FC, SVGAttributes } from "react";
import type { Store } from "store";

type StreamingEventNavigationProps = {
  isLiveCommerceEnabled: boolean;
  showCommentNavigation: boolean;
  handleClickCommentNavigation: () => void;
  handleClickGoodsNavigation: () => void;
};

type StreamingNavigationViewProps = {
  isDarkModeEnabled: boolean;
  isLiveCommerceEnabled: boolean;
  showCommentNavigation: boolean;
  goodsThumbnailUrl: string;
  goodsName: string;
  handleClickCommentNavigation: () => void;
  handleClickGoodsNavigation: () => void;
};

/**
 * イベント概要エリア
 * @param props
 */
export const StreamingEventNavigation: React.FC<
  StreamingEventNavigationProps
> = (props) => {
  const isAdmin = useSelector((state: Store) => state.admin.isAdmin);
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const goods = useSelector((state: Store) => state.ticket.eventGoods);
  const selectedLiveCommerceGoods = goods
    .filter(
      (goods) =>
        (isDisplayPeriod(
          goods.display,
          goods.releaseDateTime,
          goods.closeDateTime
        ) &&
          isShippingItem(goods)) ||
        (goods.display && isAdmin)
    )
    .sort(
      (prev, cur) =>
        cur.releaseDateTime.seconds - prev.releaseDateTime.seconds ||
        prev.priority - cur.priority
    );

  if (!selectedLiveCommerceGoods[0]) {
    return (
      <StreamingNavigationView
        isDarkModeEnabled={isDarkModeEnabled}
        isLiveCommerceEnabled={props.isLiveCommerceEnabled}
        showCommentNavigation={props.showCommentNavigation}
        goodsThumbnailUrl={""}
        goodsName={""}
        handleClickCommentNavigation={props.handleClickCommentNavigation}
        handleClickGoodsNavigation={props.handleClickGoodsNavigation}
      />
    );
  }

  const thumbnailUrl = selectedLiveCommerceGoods[0].thumbnail ?? "";
  const goodsName = selectedLiveCommerceGoods[0].name;

  return (
    <StreamingNavigationView
      isDarkModeEnabled={isDarkModeEnabled}
      isLiveCommerceEnabled={props.isLiveCommerceEnabled}
      showCommentNavigation={props.showCommentNavigation}
      goodsThumbnailUrl={thumbnailUrl}
      goodsName={goodsName}
      handleClickCommentNavigation={props.handleClickCommentNavigation}
      handleClickGoodsNavigation={props.handleClickGoodsNavigation}
    />
  );
};

export const StreamingNavigationView: React.FC<StreamingNavigationViewProps> = (
  props
) => {
  const { t } = useI18n();

  return (
    <Box
      display={"flex"}
      flexDirection={["column", "row"]}
      alignItems={"flex-start"}
      gridGap={"16px"}
      width={"100%"}
    >
      {props.showCommentNavigation && (
        <StreamingNavigationItemWrapper
          isDarkModeEnabled={props.isDarkModeEnabled}
          handleClick={props.handleClickCommentNavigation}
        >
          <Box padding={"16px"}>
            <Box display={"flex"} alignItems={"center"} gridGap={"4px"}>
              <CommentIcon
                fill={props.isDarkModeEnabled ? "#FFFFFF" : "#000000"}
                style={{
                  width: "22px",
                  height: "auto",
                }}
              />
              <Box marginBottom={"4px"}>
                {t("streaming.eventNavigation.commentListHeading")}
              </Box>
            </Box>
            <Box display={"flex"} alignItems={"center"}>
              <Box
                width={"fit-content"}
                padding={"4px 8px"}
                marginLeft={"auto"}
                color={props.isDarkModeEnabled ? "#FFFFFF" : "#000000"}
                fontSize={"12px"}
                bgcolor={props.isDarkModeEnabled ? "#000000" : "#fefefe"}
                borderRadius={"6px"}
              >
                {t("streaming.eventNavigation.openComment")}
              </Box>
            </Box>
          </Box>
        </StreamingNavigationItemWrapper>
      )}

      {props.isLiveCommerceEnabled && props.goodsName && (
        <StreamingNavigationItemWrapper
          isDarkModeEnabled={props.isDarkModeEnabled}
          handleClick={props.handleClickGoodsNavigation}
        >
          <Box display={"flex"}>
            {props.goodsThumbnailUrl && (
              <img
                src={props.goodsThumbnailUrl}
                alt={props.goodsName}
                className="goods-thumbnail"
                css={css`
                  width: 100%;
                  max-width: 80px;
                  aspect-ratio: 1 / 1;
                  object-fit: cover;
                `}
              />
            )}
            <Box
              display="flex"
              flexDirection="column"
              flex="1"
              padding="16px"
              minWidth={0}
            >
              <Box flexGrow={1} display="flex" alignItems="center">
                <Box
                  css={css`
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  `}
                >
                  {props.goodsName}
                </Box>
              </Box>

              <Box fontWeight={600}>
                {t("streaming.eventNavigation.seeMore")}
              </Box>
            </Box>
          </Box>
        </StreamingNavigationItemWrapper>
      )}
    </Box>
  );
};

const StreamingNavigationItemWrapper: FC<{
  children: React.ReactNode;
  isDarkModeEnabled: boolean;
  handleClick: () => void;
}> = ({ children, isDarkModeEnabled, handleClick }) => {
  return (
    <Box
      width={"100%"}
      maxWidth={"480px"}
      color={isDarkModeEnabled ? "#FFFFFF" : "#000000"}
      fontSize={"12px"}
      fontWeight={600}
      textAlign={"left"}
      bgcolor={isDarkModeEnabled ? "#333333" : "#F5F5F5"}
      borderRadius={"8px"}
      overflow={"hidden"}
      onClick={handleClick}
      css={css`
        cursor: pointer;
        transition: 0.5s;
        &:hover {
          background-color: ${isDarkModeEnabled ? "#444444" : "#E8E8E8"};
        }
      `}
    >
      {children}
    </Box>
  );
};

const CommentIcon: FC<SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H6L4 18V4H20V16Z"
        fill="currentColor"
      />
    </svg>
  );
};
